import React from 'react';
import NoControlsVideo from './NoControlsVideo';

/**
 * PandaLoader — looping panda video used as the brand loading indicator.
 *
 * variant:
 *   - "fullscreen": fixed overlay covering the viewport, with a backdrop
 *   - "page":       fills the parent (e.g. inside a route view), no backdrop
 *   - "inline":     fits where placed (use `size` to control)
 *
 * size: width/height of the video in px (only honored for "inline"; the
 *       fullscreen/page variants use a responsive clamp).
 *
 * The MP4 has a baked-in white background. We strip it with an SVG
 * color-matrix filter that maps near-white pixels to alpha=0 while leaving
 * the panda's actual colors completely untouched. No blend modes, so the
 * loader looks identical on light or dark surfaces.
 *
 * NoControlsVideo keeps any browser from drawing its own play button or
 * control bar over it, and where autoplay is off (iOS Low Power Mode) the
 * poster — its first frame — stands in until the next tap starts it.
 */
const PandaLoader = ({
  variant = 'fullscreen',
  size = 160,
  message,
  subMessage,
  withBackdrop = true,
  className = '',
  style = {},
}) => {
  const isFullscreen = variant === 'fullscreen';
  const isPage = variant === 'page';

  const videoSize = isFullscreen
    ? '320px'
    : isPage
      ? 'clamp(200px, 26vh, 320px)'
      : `${size}px`;

  const containerStyle = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: withBackdrop
          ? 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)'
          : 'transparent',
        isolation: 'isolate',
        fontFamily: "'Fredoka', sans-serif",
      }
    : isPage
      ? {
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Fredoka', sans-serif",
        }
      : {
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Fredoka', sans-serif",
        };

  return (
    <div className={className} style={{ ...containerStyle, ...style }} role="status" aria-live="polite">
      <NoControlsVideo
        autoPlay
        muted
        loop
        preload="auto"
        poster="/assets/loading-panda-poster.png"
        aria-label="Loading"
        style={{
          width: videoSize,
          height: videoSize,
          objectFit: 'contain',
          background: 'transparent',
          pointerEvents: 'none',
        }}
      >
        {/* Transparent WebM (preferred — has real alpha channel) */}
        <source src="/assets/loading-panda.webm" type="video/webm" />
        {/* MP4 fallback (white background — only used when WebM/alpha is unsupported) */}
        <source src="/assets/loading-panda.mp4" type="video/mp4" />
      </NoControlsVideo>
      {message && (
        <h3
          style={{
            margin: isFullscreen ? '-0.25rem 0 0' : '14px 0 0',
            color: '#0065F8',
            fontFamily: isFullscreen ? "'Caveat', cursive" : "'Fredoka', sans-serif",
            fontWeight: 700,
            fontSize: isFullscreen ? '3rem' : 'clamp(1.05rem, 1.8vw, 1.35rem)',
            letterSpacing: isFullscreen ? '0.01em' : '-0.01em',
            lineHeight: 1.1,
            textAlign: 'center',
            maxWidth: 560,
            padding: '0 16px',
          }}
        >
          {message}
        </h3>
      )}
      {subMessage && (
        <p
          style={{
            margin: '8px 0 0',
            color: '#5A6B85',
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 480,
            padding: '0 16px',
            lineHeight: 1.5,
          }}
        >
          {subMessage}
        </p>
      )}
    </div>
  );
};

export default PandaLoader;
